import type { Door } from '../../../lib/contract'
import { useT } from '../../../lib/i18n'
import theme from '../theme.module.css'
import styles from './Corridor.module.css'

/**
 * THE CORRIDOR — the guest's doors (the hall; UI spec §5.1, handoff addendum 10 item 6).
 *
 * The landing screen after the opening: which threads exist, and the way into one. The law it
 * implements is the amended one — *never a **rendered** room*: the hall is walked as a list,
 * the room is the thread, and nothing inside is ever drawn.
 *
 * ⚠ WHAT IS ABSENT IS THE DESIGN, and the wire enforces most of it (`ThreadsResponse` carries
 * none of these, so none can be rendered by accident):
 *
 *  - **No counts, ever.** Not messages, not visits, not days. The acceptance criterion is
 *    literally "no door ever carries a count".
 *  - **No times, no last-line preview.** A corridor showing the last thing said would make the
 *    landing screen a feed of the guest's own conversations — and the acceptance criterion is
 *    that it "shows names of elves and never a line the guest wrote".
 *  - **No reordering.** Fixed order of meeting, server-sent. A hall that rearranged itself by
 *    recency would be ranking the guest's relationships for them.
 *  - **No dark doors.** A room nobody has introduced is not listed at all — not greyed out,
 *    not teased. A greyed row is the product telling you what you are missing.
 *
 * ── THE REALM, THEN HOME, THEN ADVISORS (2026-08-23) ───────────────────────────────────────
 * Three plain nouns, and the guest's whole map. THE REALM is the official half — the door you
 * are received at, and the queen herself, who takes letters rather than conversation. HOME is
 * the household you were given. ADVISORS is who you have been introduced to since.
 *
 * ⚠ UNA WAS UNHEADED UNTIL THIS EVENING, on the POC note §1.2's reasoning that a heading over her
 * "would have to name a category she is the only member of". She is not the only member once the
 * queen's own door exists — so the objection dissolved rather than being overruled, and the same
 * heading adopted the letters row, which had been orphaned in a section of its own at the foot.
 *
 * ⚠ THE *ADVISORS* HEADING APPEARS WITH ITS FIRST ROW AND NEVER BEFORE. This replaced a design
 * from earlier the same day that put Una in the heading as a control, so the section could stand
 * empty and still say something. That was a way of TOLERATING an empty section; this removes it.
 * The rule underneath is the stronger one and it is worth stating plainly:
 *
 *     A name is on the list or it is not. There is no other state.
 *
 * Which is the same law as "no dark doors", one level up: not greyed, not teased, and not a
 * heading standing over nothing either.
 *
 * ⚠ HER ROSTER LINE OCCUPIES THE SLOT A MESSENGER PUTS THE LAST MESSAGE IN, and that is the
 * one borrowing from the form factor this surface is glad to make: it is an INTRODUCTION rather
 * than a preview, so it says who someone is instead of replaying what you said to them — and
 * the server nulls it once you have been in. Its absence is the whole of the opened-state.
 */
export default function Corridor({
  doors,
  onEnter,
  paper = false,
}: {
  doors: Door[]
  onEnter: (door: Door) => void
  /**
   * The parked Hildur skin — paper on ground rather than a plain list (2026-08-23).
   * See `theme.module.css`: nothing ships with this on.
   */
  paper?: boolean
}) {
  const t = useT()

  /*
   * `place` already separates her, which is why the taxonomy has a HOUSEKEEPER value distinct
   * from ROOM: she keeps the first door rather than living behind one. No wire change was needed
   * for any of this — the server had said what she was all along.
   */
  const home = doors.filter((d) => d.place === 'home')
  const advisors = doors.filter((d) => d.place === 'room')
  /*
   * THE REALM — the official half of the list: the door you are received at, and the queen
   * herself, who takes letters. Reception first, because she is the one you actually use.
   */
  const realm = [
    ...doors.filter((d) => d.place === 'housekeeper'),
    ...doors.filter((d) => d.place === 'letters'),
  ]

  /*
   * ⚠ A PACK WITH NO REALM GETS NO HEADINGS (2026-08-25 — the Talk-to-X wedge's Tier-0 packs).
   *
   * THE REALM · HOME · ADVISORS are Hildur's own three nouns — the hall's map, in her hand.
   * A pack that has no housekeeper and no letters is not a hall: it is a plain roster, and
   * calling Napoleon "home" and a troll an "advisor" would be this surface asserting a shape the
   * pack does not have.
   *
   * The test is `realm.length` rather than a pack flag on purpose. The realm section IS the
   * hall — the door you are received at and the queen who takes letters — so its presence is
   * the fact, not a proxy for one. Hildur always has Una, so Hildur's rendering is untouched to
   * the byte; the flat list is what a roster pack gets.
   *
   * ⚠ EVERYTHING ELSE ABOUT THE CORRIDOR IS STILL HILDUR-SHAPED, AND ON A COMEDY PACK THAT IS
   * ACCEPTED DEBT (build order, 2026-08-25): the ORDER is still home-then-rooms, `place` still
   * carries the hall's taxonomy on the wire, and a room still has to be "introduced".
   * Redesigning the corridor for a second pack shape is a real job and is not this one.
   *
   * ⚠ AND NO COMEDY PACK REACHES THIS FILE TODAY. Pack-per-conversation (same evening) made each
   * of the four its own one-persona pack, and a pack with one door is entered rather than drawn
   * (`surfaces/messaging/index.tsx`). The realm test above is kept for the next roster pack —
   * dead code that states a rule is cheaper than the rule being rediscovered.
   */
  const headed = realm.length > 0

  const row = (d: Door) => (
    <button key={d.personaId} className={styles.entry} onClick={() => onEnter(d)}>
      {/* THE LAMP, AS A MARK — and paper-only, because it never carried information. A door
          nobody has introduced is absent from this page entirely, so every row here is one you
          may knock on and the dot says what the row already said. */}
      {paper && <span className={styles.dot} aria-hidden="true" />}
      <span className={styles.text}>
        <span className={styles.head}>
          <span className={styles.name}>
            {/* The far end is the only row whose text is not a name: she is a letter, not a
                correspondent, so it says what you would be DOING rather than who is behind it. */}
            {d.place === 'letters' ? t('corridor.writeTo', { name: d.name }) : d.name}
          </span>
          {/* ⚠ THE TITLE IS THE PACK'S, AND IT NEVER GOES. The roster line beneath is Hildur
              introducing a stranger and the server nulls it once you have been in; this is what
              someone IS, so a settled row is still a name with a person attached to it. */}
          {d.title && <span className={styles.title}>{d.title}</span>}
        </span>
        {d.rosterLine && <span className={styles.roster}>{d.rosterLine}</span>}
      </span>
    </button>
  )

  return (
    <div
      className={paper ? `${theme.hildur} ${styles.corridor} ${styles.onPaper}` : styles.corridor}
    >
      <div className={styles.list}>
        {realm.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.heading}>{t('corridor.sectionRealm')}</h2>
            {realm.map(row)}
          </section>
        )}

        {home.length > 0 && (
          <section className={styles.section}>
            {headed && <h2 className={styles.heading}>{t('corridor.sectionHome')}</h2>}
            {home.map(row)}
          </section>
        )}

        {/* The heading arrives with the first advisor and not one visit sooner. */}
        {advisors.length > 0 && (
          <section className={styles.section}>
            {headed && <h2 className={styles.heading}>{t('corridor.sectionAdvisors')}</h2>}
            {advisors.map(row)}
          </section>
        )}

      </div>
    </div>
  )
}
